// Importing React and external libs
import React, { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

// Importing d3 libraries
import * as d3 from 'd3';
import * as fc from 'd3fc';

// Importing helix files
import { chromosomes } from 'helix-modules/chromosome';
import { BLOCK_COLORS, DISEASE_REGION_COLOR, FLANKING_COLOR, SNP_COLORS } from 'helix-styles/colors';

// Importing local component
import ActiveSnp from './activeSnp';

// Importing component style
import './style.scss';

const CHROMS = chromosomes.map(chromosome => chromosome.label);

const positionsToBlocks = ({
  colors,
  initial = 0,
  positions,
  yPosition
}) => {
  const bars = [];
  for (let index = 0; index < positions.length - 1; index++) {
    let colorIndex = 0;
    if (initial > 0) {
      const offset = index % 2;
      colorIndex = initial % 2 === 0 ? initial - offset : initial + offset;
    }
    const { fill, stroke } = colors[colorIndex];
    bars.push({
      color: fill,
      end: positions[index + 1],
      start: positions[index],
      stroke,
      yPos: yPosition,
    });
  }
  return bars;
};

const snpPhaseWebglColor = (snpColors) => (snp) => {
  const phaseValue = [2, 5].includes(snp.snp_type) ? 0 : snp.snp_phase;
  const fillRgb = d3.color(snpColors[phaseValue].fill);
  const strokeRgb = d3.color(snpColors[phaseValue].stroke);
  return {
    ...snp,
    fill: [fillRgb.r / 255, fillRgb.g / 255, fillRgb.b / 255, fillRgb.opacity],
    stroke: [strokeRgb.r / 255, strokeRgb.g / 255, strokeRgb.b / 255, strokeRgb.opacity],
  };
};

const getFlankingRegions = ({ flanking, flankingColor, flankingStrokeWidth, regions }) => regions.map(
  region => ({
    start: region.start - (flanking * 1e6),
    end: region.end + (flanking * 1e6),
    color: flankingColor.fill,
    stroke: flankingColor.stroke,
    strokeWidth: flankingStrokeWidth,
  })
);

const Haploblock = ({
  blocks,
  id,
  chrom,
  diseaseRegions,
  diseaseRegionColor,
  diseaseRegionStrokeWidth,
  domain,
  enableSnps,
  extent,
  flanking,
  flankingColor,
  flankingStrokeWidth,
  haploblockColors,
  haploblockStrokeWidth,
  height,
  mutationSiteColor,
  mutationSiteWidth,
  scale,
  snps: snpsWithoutColor,
  snpColors,
  snpSize,
  snpYOffset,
}) => {
  const chartRef = useRef(null);
  const geneRegionsRef = useRef();
  const [closest, setClosest] = useState(null);
  const [bars, setBars] = useState([]);
  const adjustedSnpSize = snpSize * scale ** 2;

  // add color to snps based on phase
  const snps = useMemo(
    () => (enableSnps ? snpsWithoutColor.map(snpPhaseWebglColor(snpColors)) : []),
    [snpsWithoutColor, snpColors]
  );

  // generate bars
  useEffect(() => {
    const father = d3.sort([extent[0], ...blocks.father.positions, extent[1]]);
    const mother = d3.sort([extent[0], ...blocks.mother.positions, extent[1]]);
    const fatherStack = positionsToBlocks({
      colors: haploblockColors,
      initial: blocks.father.initial,
      positions: father,
      yPosition: 2
    });
    const motherStack = positionsToBlocks({
      colors: haploblockColors,
      initial: blocks.mother.initial,
      positions: mother,
      yPosition: 5
    });
    setBars([...fatherStack, ...motherStack]);
  }, [blocks, extent]);

  // generate snps
  useEffect(() => {
    if (!chartRef.current) return;

    const chartEl = d3.select(chartRef.current);

    const { clientHeight, clientWidth } = chartRef.current.querySelector(
      '.plot-area'
    ) ?? { clientWidth: 100, clientHeight: 100 };

    if (clientWidth < 100 && clientHeight < 100) return;

    const xScale = d3.scaleLinear().domain(domain).range([0, clientWidth]);

    const yDomain = [7, 6, 5, 4, 3, 2, 1, 0];
    const yScale = d3
      .scalePoint()
      .domain(yDomain)
      .range([0, clientHeight]);

    const maxY = Math.max(...yDomain);

    const barHeight = clientHeight / (maxY - (enableSnps ? 0 : 3.5));

    const barSeries = fc
      .seriesSvgBar()
      .orient('horizontal')
      .align('left')
      .crossValue((dataPoint) => dataPoint.yPos)
      .mainValue((dataPoint) => dataPoint.end)
      .baseValue((dataPoint) => dataPoint.start)
      .bandwidth(barHeight)
      .decorate((selection) => {
        selection
          .attr('fill', (dataPoint) => dataPoint.color)
          .select('path')
          .attr('transform', `translate(0 ${-barHeight / 2})`)
          .attr('stroke', (dataPoint) => dataPoint.stroke)
          .attr('stroke-width', haploblockStrokeWidth)
          .attr('stroke-dasharray', (_, idx, elements) => {
            const { width, height } = elements[idx]?.getBoundingClientRect?.() ?? {};
            return `${width} ${height} ${width}`;
          });
      });

    const chart = fc
      .chartCartesian(xScale, yScale)
      .useDevicePixelRatio(false)
      .xAxisHeight('0')
      .yAxisWidth('0')
      .xTickFormat(tick => Number(tick) / 1e6)
      .svgPlotArea(
        fc
          .seriesSvgMulti()
          .series([barSeries])
          .mapping((dataPoint) => dataPoint.bars)
      );

    if (enableSnps) {
      const [start, end] = xScale.domain();
      const relativeScale = (end - start) / maxY;

      const quadtree = d3
        .quadtree()
        .x((dataPoint) => dataPoint.position)
        .y((dataPoint) => dataPoint.snp_type * relativeScale)
        .addAll(snps);

      const pointer = fc.pointer().on('point', ([coords]) => {
        if (coords && quadtree) {
          // find the closest datapoint to the pointer
          const x = xScale.invert(coords.x);
          const y = coords.y / (clientHeight / maxY);
          const rad = ((end - start) / adjustedSnpSize) * scale;
          const found = quadtree.find(x, y * relativeScale, rad);
          setClosest(found);
        } else {
          setClosest(null);
        }
      });

      const pointSeries = fc
        .seriesWebglPoint()
        .crossValue((dataPoint) => dataPoint.position)
        .mainValue((dataPoint) => dataPoint.snp_type)
        .size(adjustedSnpSize)
        .decorate((program) => {
          fc
            .webglFillColor()
            .value((dataPoint) => dataPoint.fill)
            .data(snps)(program);
          fc
            .webglStrokeColor()
            .value((dataPoint) => dataPoint.stroke)
            .data(snps)(program);
        });

      chart.webglPlotArea(
        fc
          .seriesWebglMulti()
          .series([pointSeries])
          .mapping((dataPoint) => dataPoint.snps)
      );

      chartEl.selectAll('.plot-area').call(pointer);
    }

    // Disease regions with flanking
    d3.select(geneRegionsRef.current)
      .attr('width', clientWidth)
      .attr('height', clientHeight)
      .selectAll('rect')
      .data([...getFlankingRegions({ flanking, flankingColor, flankingStrokeWidth, regions: diseaseRegions }), ...diseaseRegions])
      .join('rect')
      .attr('fill', d => d.color ?? diseaseRegionColor.fill)
      .attr('stroke', d => d.stroke ?? diseaseRegionColor.stroke)
      .attr('stroke-width', d => d.strokeWidth ?? diseaseRegionStrokeWidth)
      .attr('stroke-dasharray', d => {
        const height = clientHeight;
        const width = xScale(d.end) - xScale(d.start);
        return `0 ${width} ${height} ${width} ${height}`;
      })
      .attr('width', d => xScale(d.end) - xScale(d.start))
      .attr('height', clientHeight)
      .attr('x', d => xScale(d.start));

    // Mutation site
    d3.select(geneRegionsRef.current)
      .selectAll('line')
      .data(diseaseRegions.filter(region => {
        const isNumber = typeof region.mutationSite === 'number';
        const regionWidth = xScale(region.end) - xScale(region.start);
        return isNumber && regionWidth > parseInt(mutationSiteWidth) + parseInt(diseaseRegionStrokeWidth);
      }))
      .join('line')
      .attr('x1', d => xScale(d.mutationSite))
      .attr('x2', d => xScale(d.mutationSite))
      .attr('y1', 0)
      .attr('y2', clientHeight)
      .attr('stroke-width', mutationSiteWidth)
      .attr('stroke', mutationSiteColor);

    d3.select(chartRef.current).datum({ snps, bars: bars ?? [] }).call(chart);

    chartEl.select('.webgl-plot-area.plot-area').on('measure', event => {
      const canvas = event.target.querySelector('canvas');
      canvas.width = canvas.width * scale;
      canvas.height = canvas.height * scale;
      const gl = canvas.getContext('webgl');
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    });
  }, [bars, chartRef, closest, domain, diseaseRegions, diseaseRegionColor, enableSnps, flanking, scale, snps, snpSize]);

  return (
    <section className="haploblock" id={id}>
      <div
        className="haploblock--chart"
        ref={chartRef}
        style={{ height, minHeight: enableSnps ? '180px' : '80px' }}
      >
        <svg ref={geneRegionsRef} className="gene-regions" />
      </div>
      {enableSnps && closest && <ActiveSnp chrom={chrom} snp={closest} snpYOffset={snpYOffset} />}
    </section>
  );
};

const BLOCK_PROPTYPES_SHAPE = PropTypes.shape({
  initial: PropTypes.number.isRequired,
  positions: PropTypes.arrayOf(PropTypes.number).isRequired,
}).isRequired;

const REGION_STYLE_SHAPE = PropTypes.shape({
  fill: PropTypes.string.isRequired,
  stroke: PropTypes.string.isRequired,
});

const STROKE_WIDTH_TYPE = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

Haploblock.propTypes = {
  blocks: PropTypes.shape({
    father: BLOCK_PROPTYPES_SHAPE,
    mother: BLOCK_PROPTYPES_SHAPE,
  }).isRequired,
  id: PropTypes.string,
  chrom: PropTypes.oneOf(CHROMS).isRequired,
  domain: PropTypes.arrayOf(PropTypes.number).isRequired, // min and max pos after applying zoom and pan
  diseaseRegions: PropTypes.arrayOf(PropTypes.shape({
    start: PropTypes.number.isRequired,
    end: PropTypes.number.isRequired,
    mutationSite: PropTypes.number
  })),
  diseaseRegionColor: REGION_STYLE_SHAPE.isRequired,
  diseaseRegionStrokeWidth: STROKE_WIDTH_TYPE.isRequired,
  enableSnps: PropTypes.bool.isRequired,
  extent: PropTypes.arrayOf(PropTypes.number).isRequired, // min and max pos of chart
  flanking: PropTypes.number.isRequired,
  flankingColor: REGION_STYLE_SHAPE.isRequired,
  flankingStrokeWidth: STROKE_WIDTH_TYPE.isRequired,
  haploblockColors: PropTypes.arrayOf(REGION_STYLE_SHAPE).isRequired,
  haploblockStrokeWidth: STROKE_WIDTH_TYPE.isRequired,
  height: PropTypes.string,
  mutationSiteColor: PropTypes.string,
  mutationSiteWidth: STROKE_WIDTH_TYPE.isRequired,
  scale: PropTypes.number.isRequired,
  snps: PropTypes.arrayOf(
    PropTypes.shape({
      call: PropTypes.oneOf(['AA', 'AB', 'BB', 'NC']).isRequired,
      position: PropTypes.number.isRequired,
      snp: PropTypes.string.isRequired,
      snp_phase: PropTypes.number.isRequired,
      snp_type: PropTypes.number.isRequired,
    })
  ),
  snpSize: PropTypes.number.isRequired,
  snpColors: PropTypes.arrayOf(REGION_STYLE_SHAPE).isRequired,
  snpYOffset: PropTypes.number,
};

Haploblock.defaultProps = {
  diseaseRegions: [],
  diseaseRegionColor: DISEASE_REGION_COLOR,
  diseaseRegionStrokeWidth: '2px',
  enableSnps: false,
  flanking: 2,
  flankingColor: FLANKING_COLOR,
  flankingStrokeWidth: '2px',
  haploblockColors: BLOCK_COLORS,
  haploblockStrokeWidth: '2px',
  height: '80px',
  mutationSiteColor: '#000000',
  mutationSiteWidth: 2,
  scale: 2,
  snpSize: 36,
  snpColors: SNP_COLORS,
};

export default Haploblock;
